// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO (notifications rework 2026-10, design/notifications-rework a-agents): Settings >
// Notifications > Agents on the phone, a page of its own: icon rows for each event, then choice
// cards for which chats. Same prefs as the desktop section (Agents.prefs, saved on the server).
// In the phone app also the lock screen switch: the one Live Activity (iOS) / ongoing notification
// (Android) for all running agents, with Allow / Deny (design/agents-send-and-alerts round C).

import {SettingsIconChoiceCards} from '@app/features/app/components/dialogs/shared/SettingsIconChoiceCards';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import Agents from '@app/features/konvo_agents/state/Agents';
import {isNativeMobileShell} from '@app/features/platform/native_mobile/NativeMobileBridge';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {ArrowCounterClockwiseIcon, CheckCircleIcon, HandIcon, WarningIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/PhoneNotifications.module.css';
import type {AgentsPrefs} from '@konvo/schema/src/domains/konvo/Agents';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconGauge, IconGitPullRequest, IconLock, IconSubtask, IconTerminal2} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useId} from 'react';

const FINISHES_PART_DESCRIPTOR = msg({
	message: 'finishes',
	comment:
		'Phone Settings > Notifications, Agents row summary: one item of a list like "Finishes, fails and needs you". Lowercase; the first item is capitalised in code.',
});
const SUBAGENT_FINISHES_PART_DESCRIPTOR = msg({
	message: 'sub-agent finishes',
	comment:
		'Phone Settings > Notifications, Agents row summary: one item of a list like "Finishes, sub-agent finishes, fails and needs you". A sub-agent is a helper the main coding agent starts.',
});
const FAILS_PART_DESCRIPTOR = msg({
	message: 'fails',
	comment:
		'Phone Settings > Notifications, Agents row summary: one item of a list like "Finishes, fails and needs you".',
});
const NEEDS_YOU_PART_DESCRIPTOR = msg({
	message: 'needs you',
	comment:
		'Phone Settings > Notifications, Agents row summary: one item of a list like "Finishes, fails and needs you".',
});
const AGENTS_OFF_DESCRIPTOR = msg({
	message: 'Plan limits only',
	comment:
		'Phone Settings > Notifications, Agents row summary when finish, fail and needs-you notices are all off but plan alerts may still come.',
});
const AGENTS_LOADING_DESCRIPTOR = msg({
	message: 'Finishes, fails and needs you',
	comment: 'Phone Settings > Notifications, Agents row summary before the settings have loaded (the defaults).',
});

/** "Finishes, fails and needs you", from the person's Agents switches. */
export function agentsSummary(i18n: I18n, prefs: AgentsPrefs | null): string {
	if (!prefs) return i18n._(AGENTS_LOADING_DESCRIPTOR);
	const parts = [
		prefs.finished ? i18n._(FINISHES_PART_DESCRIPTOR) : null,
		prefs.subagentFinished ? i18n._(SUBAGENT_FINISHES_PART_DESCRIPTOR) : null,
		prefs.failed ? i18n._(FAILS_PART_DESCRIPTOR) : null,
		prefs.needsYou ? i18n._(NEEDS_YOU_PART_DESCRIPTOR) : null,
	].filter((part): part is string => part !== null);
	if (parts.length === 0) return i18n._(AGENTS_OFF_DESCRIPTOR);
	const text = new Intl.ListFormat(i18n.locale, {style: 'long', type: 'conjunction'}).format(parts);
	return text.charAt(0).toLocaleUpperCase(i18n.locale) + text.slice(1);
}

const SwitchRow: React.FC<{
	icon: React.ReactNode;
	title: string;
	description: string;
	value: boolean;
	onChange: (value: boolean) => void;
	'data-flx': string;
}> = ({icon, title, description, value, onChange, 'data-flx': dataFlx}) => {
	const labelId = useId();
	return (
		<div className={styles.row} data-flx={dataFlx}>
			<span className={styles.tile} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.text}>
				<span id={labelId} className={styles.title}>
					{title}
				</span>
				<span className={styles.description}>{description}</span>
			</span>
			<Switch compact value={value} onChange={onChange} ariaLabelledBy={labelId} data-flx={`${dataFlx}.switch`} />
		</div>
	);
};

export const PhoneAgentsPage = observer(() => {
	const {t} = useLingui();
	useEffect(() => {
		void Agents.loadPrefs();
	}, []);
	const prefs = Agents.prefs;
	if (!prefs) {
		return <p className={styles.foot}>{Agents.prefsError ? t`Couldn't load these settings.` : t`Loading…`}</p>;
	}
	const save = (patch: Partial<AgentsPrefs>) =>
		void Agents.savePrefs(patch).catch(() => ToastCommands.error(t`Could not save. Try again.`));
	return (
		<>
			<SettingsSection
				id="agents-events"
				linkable={false}
				title={t`Tell me when a chat`}
				className={styles.bare}
				data-flx="konvo-agents.notifications.phone.events"
			>
				<div className={styles.card}>
					<SwitchRow
						icon={<CheckCircleIcon size={20} />}
						title={t`Main agent finishes`}
						description={t`Done and waiting for your next prompt`}
						value={prefs.finished}
						onChange={(finished) => save({finished})}
						data-flx="konvo-agents.notifications.finished"
					/>
					<SwitchRow
						icon={<IconSubtask size={20} stroke={1.75} />}
						title={t`Sub-agent finishes`}
						description={t`One is done while the main agent keeps working`}
						value={prefs.subagentFinished === true}
						onChange={(subagentFinished) => save({subagentFinished})}
						data-flx="konvo-agents.notifications.subagent-finished"
					/>
					<SwitchRow
						icon={<HandIcon size={20} />}
						title={t`Needs me`}
						description={t`Asks for approval or an answer`}
						value={prefs.needsYou}
						onChange={(needsYou) => save({needsYou})}
						data-flx="konvo-agents.notifications.needs-you"
					/>
					<SwitchRow
						icon={<WarningIcon size={20} />}
						title={t`Fails`}
						description={t`Stops with an error`}
						value={prefs.failed}
						onChange={(failed) => save({failed})}
						data-flx="konvo-agents.notifications.failed"
					/>
				</div>
			</SettingsSection>
			{isNativeMobileShell() && (
				<SettingsSection id="agents-lock-screen" linkable={false} title={t`Lock screen`} className={styles.bare}>
					<div className={styles.card}>
						<SwitchRow
							icon={<IconLock size={20} stroke={1.75} />}
							title={t`Live view of your agents`}
							description={t`What runs and what needs you, with Allow and Deny`}
							value={prefs.liveActivity !== false}
							onChange={(liveActivity) => save({liveActivity})}
							data-flx="konvo-agents.notifications.live-activity"
						/>
					</div>
				</SettingsSection>
			)}
			<SettingsSection id="agents-plans" linkable={false} title={t`Plans`} className={styles.bare}>
				<div className={styles.card}>
					<SwitchRow
						icon={<IconGauge size={20} stroke={1.75} />}
						title={t`Nearly used up`}
						description={t`Claude or Codex at 90% of a limit`}
						value={prefs.usageAlert !== false}
						onChange={(usageAlert) => save({usageAlert})}
						data-flx="konvo-agents.notifications.usage-alert"
					/>
					<SwitchRow
						icon={<ArrowCounterClockwiseIcon size={20} />}
						title={t`Limit resets`}
						description={t`Paused chats can go on`}
						value={prefs.usageReset !== false}
						onChange={(usageReset) => save({usageReset})}
						data-flx="konvo-agents.notifications.usage-reset"
					/>
				</div>
			</SettingsSection>
			<SettingsSection
				id="agents-pulls"
				linkable={false}
				title={t({message: 'Pull requests', comment: 'Agents list switch: GitHub pull requests in every repository.'})}
				className={styles.bare}
			>
				<div className={styles.card}>
					<SwitchRow
						icon={<IconGitPullRequest size={20} stroke={1.75} />}
						title={t({
							message: 'Reviews, checks and merges',
							comment:
								'Notification settings, Agents: pushes about pull requests (asked to review, checks failed or ready on yours, reviews, merges, comments).',
						})}
						description={t({
							message: 'Asked to review, and checks, reviews and merges on yours',
							comment: 'Phone notification settings, Agents: under the pull request pushes switch.',
						})}
						value={prefs.pullUpdates !== false}
						onChange={(pullUpdates) => save({pullUpdates})}
						data-flx="konvo-agents.notifications.pull-updates"
					/>
				</div>
			</SettingsSection>
			<SettingsSection id="agents-scope" linkable={false} title={t`Which chats`} className={styles.bare}>
				<SettingsIconChoiceCards<AgentsPrefs['scope']>
					value={prefs.scope}
					onChange={(scope) => save({scope})}
					aria-label={t`Which chats`}
					note={prefs.scope === 'all' ? t`Every chat on your machines` : t`Chats you started or prompted from Konvo`}
					options={[
						{value: 'all', name: t`All chats`, icon: <IconTerminal2 size={20} stroke={1.75} />},
						{value: 'konvo', name: t`Only from Konvo`, icon: <KonvoLogo size={20} />},
					]}
					data-flx="konvo-agents.notifications.scope"
				/>
			</SettingsSection>
		</>
	);
});
